import { createSlice } from "@reduxjs/toolkit";
import { lessonService } from "../services/lessonService";
import { createCustomAsyncThunk, defaultEntityAdapter } from "./utils";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

export const getLessons = createCustomAsyncThunk(
    "lessons/get",
    async params => await lessonService.get(params)
)

export const createLesson = createCustomAsyncThunk(
    "lessons/create",
    async data => await lessonService.post(data)
)

export const updateLesson = createCustomAsyncThunk(
    "lessons/update",
    async data => await lessonService.patch(data)
)

export const deleteLesson = createCustomAsyncThunk(
    "lessons/delete",
    async data => await lessonService.delete(data)
)

export const deleteLessons = createCustomAsyncThunk(
    "lessons/deleteMany",
    async data => await lessonService.deleteMany(data)
)

export const copyLessons = createCustomAsyncThunk(
    "lessons/copy",
    async data => await lessonService.copy(data)
)

const lessonsSlice = createSlice({
    name: "lessons",
    initialState: defaultEntityAdapter.getInitialState({
        isLoading: false,
        error: null,
        current: null,
        page: 1,
        pageSize: 50,
        total: 0,
    }),
    reducers: {
        setPage: (state, action) => { state.page = action.payload },
        setPageSize: (state, action) => { state.pageSize = action.payload },
        setCurrentLesson: (state, action) => { state.current = action.payload },
        resetCurrentLesson: state => { state.current = null }
    },
    extraReducers: builder => {
        builder
            .addCase(getLessons.pending, state => { state.isLoading = true })
            .addCase(getLessons.fulfilled, (state, action) => {
                defaultEntityAdapter.setAll(state, action.payload.results)
                state.total = action.payload.count
                state.isLoading = false
            })
            .addCase(getLessons.rejected, (state, action) => {
                state.error = action.error
                state.isLoading = false
            })

            .addCase(createLesson.pending, state => { state.isLoading = true })
            .addCase(createLesson.fulfilled, (state, action) => {
                defaultEntityAdapter.addOne(state, action.payload)
                state.isLoading = false
            })
            .addCase(createLesson.rejected, (state, action) => {
                state.error = action.error
                state.isLoading = false
            })

            .addCase(updateLesson.pending, state => { state.isLoading = true })
            .addCase(updateLesson.fulfilled, (state, action) => {
                defaultEntityAdapter.upsertOne(state, action.payload)
                state.isLoading = false
            })
            .addCase(updateLesson.rejected, (state, action) => {
                state.error = action.error
                state.isLoading = false
            })

            .addCase(deleteLesson.pending, state => { state.isLoading = true })
            .addCase(deleteLesson.fulfilled, (state, action) => {
                defaultEntityAdapter.removeOne(state, action.payload.id)
                state.isLoading = false
            })
            .addCase(deleteLesson.rejected, (state, action) => {
                state.error = action.error
                state.isLoading = false
            })

            .addCase(deleteLessons.pending, state => { state.isLoading = true })
            .addCase(deleteLessons.fulfilled, (state, action) => {
                defaultEntityAdapter.removeMany(state, action.payload)
                state.isLoading = false
            })
            .addCase(deleteLessons.rejected, (state, action) => {
                state.error = action.error
                state.isLoading = false
            })
    }
})

export const { setCurrentLesson, resetCurrentLesson, setPage, setPageSize } = lessonsSlice.actions

export const lessonSelectors = defaultEntityAdapter.getSelectors(x => x.lessons)

export default lessonsSlice.reducer

export const useLessons = (userId, role, group, subject, dateBefore, dateAfter, timeBefore, timeAfter, mainGroup, studyYear) => {

    const currentLesson = useSelector(x => x.lessons.current)
    const lessons = useSelector(lessonSelectors.selectAll)
    const { page, pageSize, isLoading, total } = useSelector(x => x.lessons)

    const dispatch = useDispatch()

    const refetchLessons = () => {
        if (userId && role) {
            const params = { [role]: userId, page, page_size: pageSize }
            if (group) params.group = group
            if (subject) params.subject = subject
            if (dateBefore) params.date_before = dateBefore
            if (dateAfter) params.date_after = dateAfter
            if (timeBefore) params.time_before = timeBefore
            if (timeAfter) params.time_after = timeAfter
            if (mainGroup !== undefined) params.main_group = mainGroup
            if (studyYear) params.study_year = studyYear
            dispatch(getLessons(params))
        }
    }

    React.useEffect(() => {
        if (userId && role) {
            const params = { [role]: userId, page, page_size: pageSize }
            if (group) params.group = group
            if (subject) params.subject = subject
            if (dateBefore) params.date_before = dateBefore
            if (dateAfter) params.date_after = dateAfter
            if (timeBefore) params.time_before = timeBefore
            if (timeAfter) params.time_after = timeAfter
            if (mainGroup !== undefined) params.main_group = mainGroup
            if (studyYear) params.study_year = studyYear
            dispatch(getLessons(params))
        }
        return () => dispatch(resetCurrentLesson())
    }, [page, pageSize, role, userId, group, subject, dateBefore, dateAfter, timeBefore, timeAfter, mainGroup, studyYear, dispatch])

    const setLessonsPage = page => dispatch(setPage(page))

    return { lessons, refetchLessons, currentLesson, lessonsIsLoading: isLoading, lessonsPage: page, setLessonsPage, lessonsPageSize: pageSize, lessonsTotal: total }
}
